<template>
  <main class="sms-tool">
    <section class="sms-editor">
      <div class="container-fluid tool-container">
        <div class="tool-heading">
          <span>NetGSM aracı</span>
          <h1>Toplu SMS Gönder</h1>
          <p>İsim ve telefon listesini ekleyin, mesajınızı hazırlayın ve NetGSM API üzerinden toplu SMS gönderin.</p>
        </div>

        <div class="sms-layout">
          <form class="sms-form" @submit.prevent="sendSms">
            <section class="form-panel">
              <div class="panel-title">
                <i class="fa fa-users"></i>
                <h2>Rehber Seçimi</h2>
              </div>

              <div class="directory-switch">
                <button
                  v-for="directory in directories"
                  :key="directory.key"
                  type="button"
                  class="directory-button"
                  :class="{ active: selectedDirectory === directory.key }"
                  @click="selectDirectory(directory.key)"
                >
                  <span>{{ directory.label }}</span>
                  <strong>{{ directoryCounts[directory.key] }}</strong>
                </button>
              </div>

              <small class="helper-text">
                Seçilen rehberdeki kişiler sağ tarafta listelenir. Rehber değişiklikleri site dosyalarındaki SMS JSON listesinden yapılır.
              </small>
            </section>

            <section class="form-panel">
              <div class="panel-title">
                <i class="fa fa-message"></i>
                <h2>Mesaj</h2>
              </div>

              <label for="message" class="form-label">SMS metni</label>
              <textarea id="message" v-model="message" class="form-control" rows="7" placeholder="SMS metnini yazın." />
              <div class="message-meta">
                <span>{{ message.length }} karakter</span>
                <span>{{ estimatedSmsParts }} SMS boyu tahmini</span>
                <span v-if="message.includes('{ad}')">Kişiye özel: ad kullanılıyor</span>
              </div>
            </section>

            <section class="send-panel">
              <button class="btn btn-primary" type="submit" :disabled="isSending || !canSend">
                <i class="fa fa-paper-plane me-2"></i>{{ isSending ? 'Gönderiliyor...' : `${activeDirectoryLabel} Rehberine SMS Gönder` }}
              </button>
            </section>
          </form>

          <aside class="sms-preview">
            <section class="summary-card">
              <div>
                <span>Geçerli Alıcı</span>
                <strong>{{ recipients.length }}</strong>
              </div>
              <div>
                <span>SMS Başlığı</span>
                <strong class="summary-text">{{ msgheader }}</strong>
              </div>
              <div>
                <span>Rehber</span>
                <strong class="summary-text">{{ activeDirectoryLabel }}</strong>
              </div>
            </section>

            <section class="preview-card">
              <div class="preview-head">
                <h2>Gönderim Önizleme</h2>
                <span>{{ payload.messages.length }} mesaj</span>
              </div>

              <div class="sms-phone">
                <div class="phone-top"></div>
                <div class="bubble">
                  {{ previewMessage }}
                </div>
              </div>
            </section>

            <section class="table-card">
              <div class="preview-head">
                <h2>Alıcılar</h2>
                <span>{{ recipients.length }} kişi</span>
              </div>

              <div class="recipient-table">
                <div v-for="item in recipients" :key="item.no" class="recipient-row">
                  <span>{{ item.name || 'İsimsiz' }}</span>
                  <strong>{{ item.no }}</strong>
                </div>
                <div v-if="!recipients.length" class="recipient-empty">
                  Bu rehberde kayıtlı alıcı yok.
                </div>
              </div>
            </section>

            <section v-if="result" class="result-card" :class="{ success: result.code === '00', error: result.code !== '00' }">
              <h2>NetGSM Cevabı</h2>
              <pre>{{ resultText }}</pre>
            </section>
          </aside>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, ref } from 'vue'
import SMS_DIRECTORIES from '@/data/smsDirectories'

const username = '3129118159'
const password = 'Y1-wy9yF'
const msgheader = 'AGRI DERNEK'
const selectedDirectory = ref('yonetim')
const message = ref('Sayın {ad}, Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği olarak toplantımıza katılımınızı bekleriz.')
const isSending = ref(false)
const result = ref(null)
const directories = [
  { key: 'yonetim', label: 'Yönetim' },
  { key: 'uyeler', label: 'Üyeler' }
]

const normalizePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length === 10 && digits.startsWith('5')) return digits
  if (digits.length === 11 && digits.startsWith('05')) return digits.slice(1)
  if (digits.length === 12 && digits.startsWith('90')) return digits.slice(2)
  if (digits.length === 13 && digits.startsWith('090')) return digits.slice(3)
  return ''
}

const normalizeDirectory = (items = []) => {
  const seen = new Set()
  return items
    .map((item) => ({
      name: item.name,
      no: normalizePhone(item.phone)
    }))
    .filter((item) => item.no)
    .filter((item) => {
      if (seen.has(item.no)) return false
      seen.add(item.no)
      return true
    })
}

const recipients = computed(() => normalizeDirectory(SMS_DIRECTORIES[selectedDirectory.value]))
const directoryCounts = computed(() => ({
  yonetim: normalizeDirectory(SMS_DIRECTORIES.yonetim).length,
  uyeler: normalizeDirectory(SMS_DIRECTORIES.uyeler).length
}))

const activeDirectoryLabel = computed(() => directories.find((item) => item.key === selectedDirectory.value)?.label || 'Seçili')

const renderMessage = (recipient) => {
  const firstName = (recipient?.name || '').split(' ').filter(Boolean)[0] || 'Hemşehrimiz'
  return message.value.replaceAll('{ad}', firstName).trim()
}

const payload = computed(() => {
  const data = {
    msgheader,
    messages: recipients.value.map((recipient) => ({
      msg: renderMessage(recipient),
      no: recipient.no
    })),
    encoding: 'TR',
    iysfilter: '0',
    appname: 'AAD-Tools'
  }

  return data
})

const previewMessage = computed(() => renderMessage(recipients.value[0]))
const estimatedSmsParts = computed(() => Math.max(1, Math.ceil((message.value.length || 1) / 155)))
const canSend = computed(() => (
  recipients.value.length > 0 &&
  Boolean(message.value.trim())
))
const resultText = computed(() => JSON.stringify(result.value, null, 2))

const basicAuth = computed(() => {
  const raw = `${username}:${password}`
  return btoa(unescape(encodeURIComponent(raw)))
})

const selectDirectory = (key) => {
  selectedDirectory.value = key
}

const sendSms = async () => {
  if (!canSend.value) return
  isSending.value = true
  result.value = null

  try {
    const response = await fetch('https://api.netgsm.com.tr/sms/rest/v2/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ${basicAuth.value}`
      },
      body: JSON.stringify(payload.value)
    })

    const text = await response.text()
    try {
      result.value = JSON.parse(text)
    } catch {
      result.value = { code: String(response.status), description: text || response.statusText }
    }
  } catch (error) {
    result.value = {
      code: 'BROWSER',
      description: 'Tarayıcı isteği gönderemedi. NetGSM CORS veya API IP kısıtı uyguluyorsa küçük bir backend/proxy üzerinden göndermek gerekir.',
      detail: error?.message || String(error)
    }
  } finally {
    isSending.value = false
  }
}
</script>

<style scoped>
.sms-tool {
  background: #f5f7fb;
  min-height: 100vh;
}

.sms-editor {
  padding: 52px 0 88px;
}

.tool-container {
  max-width: 1760px;
  padding-left: 28px;
  padding-right: 28px;
}

.tool-heading {
  max-width: 980px;
  margin-bottom: 28px;
}

.tool-heading span {
  color: var(--primary);
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.tool-heading h1 {
  font-size: clamp(32px, 5vw, 54px);
  font-weight: 900;
  letter-spacing: 0;
  margin: 8px 0 10px;
}

.tool-heading p {
  color: #666565;
  font-size: 17px;
  margin: 0;
}

.sms-layout {
  display: grid;
  grid-template-columns: minmax(420px, 560px) minmax(620px, 1fr);
  gap: 28px;
  align-items: start;
}

.sms-form,
.sms-preview {
  display: grid;
  gap: 18px;
}

.form-panel,
.preview-card,
.table-card,
.result-card,
.summary-card,
.send-panel {
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
  box-shadow: 0 18px 45px rgba(15, 23, 43, .08);
}

.form-panel {
  display: grid;
  gap: 16px;
  padding: 22px;
}

.panel-title,
.preview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.panel-title {
  justify-content: flex-start;
}

.panel-title i {
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: var(--primary);
  border-radius: 8px;
}

.panel-title h2,
.preview-head h2 {
  color: var(--dark);
  font-size: 21px;
  font-weight: 900;
  margin: 0;
}

.form-label {
  color: var(--dark);
  font-weight: 800;
}

.form-control {
  border-radius: 6px;
  min-height: 48px;
}

.directory-switch {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.directory-button {
  min-height: 74px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  color: var(--dark);
  background: #f5f7fb;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
  padding: 14px 16px;
  font-weight: 900;
  transition: border-color .2s ease, background .2s ease, color .2s ease;
}

.directory-button.active {
  color: #fff;
  background: var(--dark);
  border-color: var(--dark);
}

.directory-button strong {
  min-width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: var(--primary);
  border-radius: 8px;
}

.helper-text,
.message-meta {
  color: #666565;
  font-size: 13px;
  font-weight: 700;
}

.message-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.message-meta span {
  background: #f5f7fb;
  border-radius: 999px;
  padding: 7px 10px;
}

.send-panel {
  display: grid;
  gap: 12px;
  padding: 22px;
}

.confirm-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #555;
  font-weight: 800;
}

.confirm-check input {
  margin-top: 4px;
}

.send-panel .btn {
  min-height: 52px;
}

.summary-card {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
}

.summary-card div {
  display: grid;
  gap: 4px;
  background: #fff;
  padding: 20px;
}

.summary-card span,
.preview-head span {
  color: #666565;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.summary-card strong {
  color: var(--dark);
  font-size: 34px;
  font-weight: 900;
}

.summary-card .summary-text {
  font-size: 18px;
  line-height: 1.25;
}

.preview-card,
.table-card,
.result-card {
  display: grid;
  gap: 18px;
  padding: 22px;
}

.sms-phone {
  min-height: 340px;
  display: grid;
  align-content: start;
  gap: 20px;
  background: linear-gradient(145deg, var(--dark), #1d2a45);
  border-radius: 8px;
  padding: 28px;
}

.phone-top {
  width: 86px;
  height: 8px;
  justify-self: center;
  background: rgba(255, 255, 255, .28);
  border-radius: 999px;
}

.bubble {
  max-width: 82%;
  color: var(--dark);
  background: #fff;
  border-left: 6px solid var(--primary);
  border-radius: 8px;
  padding: 18px;
  font-size: 17px;
  line-height: 1.5;
  white-space: pre-line;
}

.recipient-table {
  max-height: 360px;
  overflow: auto;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
}

.recipient-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(15, 23, 43, .06);
}

.recipient-row:last-child {
  border-bottom: 0;
}

.recipient-row span {
  color: var(--dark);
  font-weight: 800;
}

.recipient-row strong {
  color: #666565;
  font-weight: 900;
}

.recipient-empty {
  color: #666565;
  font-weight: 800;
  padding: 18px;
  text-align: center;
}

.result-card.success {
  border-left: 5px solid #16a34a;
}

.result-card.error {
  border-left: 5px solid #dc2626;
}

.result-card pre {
  color: var(--dark);
  background: #f5f7fb;
  border-radius: 8px;
  margin: 0;
  padding: 14px;
  white-space: pre-wrap;
}

@media (max-width: 1199.98px) {
  .sms-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 991.98px) {
  .sms-editor {
    padding: 36px 0 96px;
  }

  .tool-container {
    padding-left: 12px;
    padding-right: 12px;
  }

  .summary-card {
    grid-template-columns: 1fr;
  }
}
</style>
